/**
 * Indicators, grouped under one heading each.
 *
 * The picker used to emit a heading whenever an entry's category differed from
 * the previous one's — which groups correctly only if the list arrives sorted by
 * category, and it does not. `/ind/catalog` returns the server's table order,
 * which runs `…rsi, macd, stoch, adx, atr, cci, mfi, obv, roc, willr…`: Momentum
 * interrupted by Volatility and Volume and started again each time, so
 * "Momentum Indicators" appeared four separate times.
 *
 * The table is not reordered to fix it. Stroke colours are assigned by position
 * in that table (`chart_indicators._STROKE_INDEX`), so moving an entry repaints
 * every indicator below it — a display problem is not worth changing what colour
 * someone's RSI has been for a month.
 *
 * ## Why a fixed order rather than first-appearance
 *
 * Grouping alone would order the headings by whichever category happened to
 * appear first, putting Momentum fourth behind Overlap Studies, Volatility and
 * Volume. A trader reaches for momentum long before a statistic function, which
 * is the same reasoning `CATEGORY_ORDER` in the DSL registry already records for
 * the screener's picker. Categories not on the list keep their first-appearance
 * position at the end, so a category added on the server appears without a
 * client change rather than vanishing.
 *
 * Flat indices are carried through because the keyboard walks the *matches*
 * list, not the groups: headings are not selectable, and an index into a nested
 * structure would have to be flattened again on every arrow press.
 */

import type { IndicatorEntry } from "../api/indicators";

/** Heading order. Mirrors `CATEGORY_ORDER` in `services/quevee/dsl/functions.py`. */
export const CATEGORY_ORDER: readonly string[] = [
  "Overlap Studies",
  "Momentum Indicators",
  "Volatility Indicators",
  "Volume Indicators",
  "Statistic Functions",
];

export interface IndicatorGroup {
  category: string;
  /** Entries with their index into the flat list this was built from. */
  items: Array<{ entry: IndicatorEntry; index: number }>;
}

/**
 * One group per category, each appearing exactly once.
 *
 * Entries keep their relative order within a category, so the server still
 * decides what comes first under a heading.
 */
export function groupByCategory(entries: readonly IndicatorEntry[]): IndicatorGroup[] {
  const groups = new Map<string, IndicatorGroup>();

  entries.forEach((entry, index) => {
    const category = entry.category || "Other";
    const group = groups.get(category) ?? { category, items: [] };
    group.items.push({ entry, index });
    groups.set(category, group);
  });

  const rank = (category: string) => {
    const known = CATEGORY_ORDER.indexOf(category);
    // Unknown categories sort after every known one, and among themselves by
    // the order they arrived — `Map` preserves insertion, so no tiebreak is
    // needed beyond a stable sort.
    return known === -1 ? CATEGORY_ORDER.length : known;
  };

  return [...groups.values()].sort((a, b) => rank(a.category) - rank(b.category));
}
